import { FormControl, Select, Container, Grid, MenuItem, Button, TextField, AppBar, Box, Typography, Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import { ChangeEvent, useState, useEffect, FocusEvent } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import isEmail from 'validator/lib/isEmail';
import * as CognitoService from '../services/CognitoService';
import * as CountryListHelper from '../library/CountryListHelper';
import * as LanguageListHelper from '../library/LanguageListHelper';
import * as Errors from "../library/Errors";
import UserAccount from '../classes/UserAccount';
import vokey_design from '../images/Vokey-Design-Black.png';

class PasswordValidator {
    constructor(public eightChars = false, public number = false, public uppercase = false, public lowercase = false, public specialCharacter = false, public matching = false) { }
}

export default function CreateAccount() {
    const [userAccount, setUserAccount] = useState(new UserAccount())
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [emailIsValid, setEmailIsValid] = useState(false);
    const [firstNameIsValid, setFirstNameIsValid] = useState(false);
    const [lastNameIsValid, setLastNameIsValid] = useState(false);
    const [locationIsValid, setLocationIsValid] = useState(false);
    const [countryIsValid, setCountryIsValid] = useState(false);
    const [showEmailError, setShowEmailError] = useState(false);  //don't show email format error till onBlur
    const [passwordValidator, setPasswordValidator] = useState(new PasswordValidator());  //flags for what constitutes a valid password
    const [pageErrorMessage, setPageErrorMessage] = useState('');
    const [showAuthenticatedPopup, setShowAuthenticatedPopup] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [usernameTaken, setUsernameTaken] = useState(false);

    const intl = useIntl();
    const intlNewAccount = intl.formatMessage({ id: "general.new.account" });
    const intlCancel = intl.formatMessage({ id: "general.cancel" });
    const intlContinue = intl.formatMessage({ id: "general.continue" });
    const intlCreateAccount = intl.formatMessage({ id: "general.create.account" });
    const intlFirstNameRequired = intl.formatMessage({ id: "general.first.name.required" });
    const intlLastNameRequired = intl.formatMessage({ id: "general.last.name.required" });
    const intlLocationRequired = intl.formatMessage({ id: "general.full.location.required" });
    const intlGeneralEmailLc = intl.formatMessage({ id: "general.email.lc" });
    const intlEmailInvalid = intl.formatMessage({ id: "general.invalid.credentials" });
    const intlPassword = intl.formatMessage({ id: "general.password.lc" });
    const intlConfirmPassword = intl.formatMessage({ id: "general.confirm.password" });
    const intlEmailExists = intl.formatMessage({ id: "general.email.exists" });
    const intlCorrectFormErrors = intl.formatMessage({ id: "general.correct.form.errors" });
    const intlFirstName = intl.formatMessage({ id: "general.first.name" });
    const intlLastName = intl.formatMessage({ id: "general.last.name" });
    const intlMinCharacters = intl.formatMessage({ id: "general.min.characters" });
    const intlOneNumber = intl.formatMessage({ id: "general.one.number" });
    const intlLowercase = intl.formatMessage({ id: "general.lowercase" });
    const intlUppercase = intl.formatMessage({ id: "general.uppercase" });
    const intlSpecialCharacters = intl.formatMessage({ id: "general.special.characters" });
    const intlPasswords = intl.formatMessage({ id: "general.passwords" });
    const intlCountry = intl.formatMessage({ id: "general.country" });
    const intlCountryRequired = intl.formatMessage({ id: "general.country.required" });
    const intlLanguage = intl.formatMessage({ id: "general.language" });
    const intlLocationName = intl.formatMessage({ id: "general.location.name" });
    const intlShipToNumber = intl.formatMessage({ id: "general.ship.to.number" });
    const intlThankYouRegistering = intl.formatMessage({ id: "general.thank.you.registering" });
    const intlConfirmationLink = intl.formatMessage({ id: "general.confirmation.link" });

    useEffect(() => {
        if (window.location.hostname === 'localhost') {
            setUserAccount(new UserAccount("Brian", "Brian", "brian999+@gmail.com", "Thooka6M###"));
            setConfirmPassword('Thooka6M###');
        }
    }, []);

    const handleFormChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = target;
        let newUserAccount = { ...userAccount, [name]: value } as UserAccount;
        validatePage(newUserAccount); //refresh form error messages if necessary
        setUserAccount(newUserAccount);
    }

    //set the Confirm Password value and compare it to original password
    const handleConfirmPassword = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        setConfirmPassword(target.value);
        validatePassword(userAccount.password, target.value);
    }

    const handlePassword = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        let newPassword = target.value;
        let newUserAccount = { ...userAccount, password: newPassword };
        setUserAccount(newUserAccount);
        validatePassword(newPassword, confirmPassword);
        validatePage(newUserAccount);
    }

    //set validation flags
    const validatePassword = (password: string, confirmPassword: string) => {
        let newPasswordValidator = { ...passwordValidator };
        newPasswordValidator.eightChars = password.length >= 8;
        newPasswordValidator.number = password.match('[0-9]') != null;
        newPasswordValidator.lowercase = password.match('[a-z]') != null;
        newPasswordValidator.uppercase = password.match('[A-Z]') != null;
        newPasswordValidator.specialCharacter = password.match('[\*_\^\.!@\+=#&\$]') != null;
        newPasswordValidator.matching = password !== "" && password === confirmPassword;
        setPasswordValidator(newPasswordValidator);
        let isValid = newPasswordValidator.eightChars && newPasswordValidator.number && newPasswordValidator.lowercase && newPasswordValidator.uppercase && newPasswordValidator.specialCharacter && newPasswordValidator.matching;
        return isValid;
    }

    const handleEmailBlur = ({ target }: FocusEvent<HTMLInputElement>): void => {
        //can show email is invalid after they exit the input field
        validateEmail(target.value);
        setShowEmailError(true);
    }

    const validateName = (firstName: string, lastName: string) => {
        let validFirstName = firstName != "";
        setFirstNameIsValid(validFirstName);
        let validLastName = lastName != "";
        setLastNameIsValid(validLastName);
        return { validFirstName, validLastName };
    }

    const validateLocation = (location: string) => {
        let isValid = location != "";
        setLocationIsValid(isValid);
        return isValid;
    }

    const validateCountry = (country: string) => {
        let isValid = country != "-";
        setCountryIsValid(isValid);
        return isValid;
    }

    const validatePage = (userAccount: UserAccount): boolean => {
        const { validFirstName, validLastName } = validateName(userAccount.firstName, userAccount.lastName);
        let validCountry = validateCountry(userAccount.country);
        let validLocation = validateLocation(userAccount.fittingLocation);
        let validPassword = validatePassword(userAccount.password, confirmPassword);
        let validEmail = validateEmail(userAccount.email);

        let isValid = validFirstName && validLastName && validCountry && validLocation && validPassword && validEmail;
        setPageErrorMessage(!isValid ? intlCorrectFormErrors : "");
        return isValid;
    }


    const validateEmail = (email: string): boolean => {
        let validEmail = email !== "" && isEmail(email);
        setEmailIsValid(validEmail)
        return validEmail;
    }

    const navigate = useNavigate();

    const handleCancel = () => {
        navigate('/apps/wedgefitting');
    }

    const handleSubmit = async () => {
        let isValid = validatePage(userAccount);
        setShowErrors(!isValid);

        if (isValid) {
            try {
                setShowSpinner(true);
                await CognitoService.createUser2(userAccount);
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'vwf_create_account',
                        country: userAccount.country,
                        loc_name: userAccount.fittingLocation
                    }
                })
                setShowSpinner(false);
                setShowAuthenticatedPopup(true);
            }
            catch (err) {
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'log_error',
                        descricption: 'Create Account - submit',
                        msg: err
                    }
                });
                setShowSpinner(false);

                if (err instanceof Errors.UsernameTakenError) {
                    setUsernameTaken(true);
                } else if (err.name === "UsernameExistsException") {
                    setPageErrorMessage(intlEmailExists);
                } else {
                    setPageErrorMessage(err.message);
                }
                setShowErrors(true);
            }
        }
    }

    const handleDialogClose = () => {
        navigate('/apps/wedgefitting', { state: { selectedLocale: userAccount.locale }});
    }

    return (
        <div className='login-form' style={{ paddingBottom: "200px" }}>
            <div className='clear-navbar'>
                <Container component="main" maxWidth="lg">
                    <Box sx={{ flexGrow: 1 }} className='navbar'>
                        <AppBar position="static" className='appbar'>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className='name' variant="h6" component="div" sx={{ display: { xs: 'none', md: 'flex', marginTop: '15px' } }}>
                                        {intlNewAccount}
                                    </Typography>

                                </Grid>
                                <Grid item sm={4}>
                                    <img style={{ paddingTop: '15px', width: '300px', alignContent: 'center' }} src={vokey_design} />
                                </Grid>
                                <Grid item sm={4} container justifyContent="flex-end" sx={{ paddingTop: '13px', height: '24px', color: 'black' }}>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleCancel} sx={{ height: '45px' }} className='cancel-button'>{intlCancel}</Button>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} sx={{ height: '45px', width: '150px !important' }} className='submit-button'>{intlCreateAccount}</Button>
                                </Grid>
                            </Grid>
                        </AppBar>
                    </Box>
                </Container>
            </div>

            <Container component="main" maxWidth="lg" className='container' sx={{ textAlign: 'left' }}>
                <Grid container className='row-error'>
                    {!showSpinner && <>
                        <Grid item sm={12} className='form-error-label' sx={{ height: '1px' }}>
                            <span>{`${showErrors ? pageErrorMessage : ""}`}</span>
                            {usernameTaken && (
                                <span>Username already taken. Please reset your password using the email you just entered by <Link to='/apps/wedgefitting/forgotpassword'>clicking here</Link>.</span>
                            )}
                        </Grid>

                    </>
                    }
                    {showSpinner && <>
                        <Grid item sm={12} className='form-error-label' sx={{ height: '1px', textAlign: 'center !important' }}>
                            <span className="sk-wave" style={{ display: 'inline-flex', marginRight: '0px', marginTop: '10px', textAlign: 'center' }} >
                                <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                                <span className="sk-wave-rect" style={{ backgroundColor: "black" }}></span>
                            </span>
                        </Grid>
                    </>}
                </Grid>
                <Grid container>
                    {/* First Name (Given Name) */}
                    <Grid item xs={12}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlFirstName}
                        </Typography>
                        <TextField
                            value={userAccount.firstName}
                            margin="normal"
                            required
                            id="firstName"
                            name="firstName"
                            onChange={handleFormChange}
                            sx={{ width: '400px', textAlign: 'left', marginBottom: '10px !important' }}
                        />
                        <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                            {`${!firstNameIsValid && showErrors ? intlFirstNameRequired : ""}`}
                        </Typography>
                    </Grid>
                    {/* Last Name (Family Name) */}
                    <Grid item xs={12}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlLastName}
                        </Typography>
                        <TextField
                            value={userAccount.lastName}
                            margin="normal"
                            required
                            id="lastName"
                            name="lastName"
                            onChange={handleFormChange}
                            sx={{ width: '400px', textAlign: 'left', marginBottom: '10px !important' }}
                        />
                        <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                            {`${!lastNameIsValid && showErrors ? intlLastNameRequired : ""}`}
                        </Typography>
                    </Grid>
                    {/* Email */}
                    <Grid item xs={12}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlGeneralEmailLc}
                        </Typography>
                        <TextField
                            margin="normal"
                            required
                            id="email"
                            name="email"
                            value={userAccount.email}
                            onChange={handleFormChange}
                            onBlur={handleEmailBlur}
                            sx={{ width: '400px', textAlign: 'left', marginBottom: '10px !important' }}
                        />
                        <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                            {`${!emailIsValid && (showEmailError || showErrors) ? intlEmailInvalid : ""}`}
                        </Typography>

                    </Grid>
                    <Grid item xs={6}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlPassword}
                        </Typography>
                        <TextField
                            value={userAccount.password}
                            margin="normal"
                            required
                            id="password"
                            name="password"
                            type="password"
                            onChange={handlePassword}
                            sx={{ width: '400px', textAlign: 'left', marginBottom: '0px !important' }}
                        />

                    </Grid>
                    <Grid item xs={6}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlConfirmPassword}
                        </Typography>
                        <TextField
                            value={confirmPassword}
                            margin="normal"
                            required
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            onChange={handleConfirmPassword}
                            sx={{ width: '400px', textAlign: 'left', marginBottom: '0px !important' }}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ marginBottom: "40px" }}>
                        <ul className="password">
                            <li className={passwordValidator.eightChars ? "valid" : "invalid"}>{intlMinCharacters}</li>
                            <li className={passwordValidator.number ? "valid" : "invalid"}>{intlOneNumber}</li>
                            <li className={passwordValidator.lowercase ? "valid" : "invalid"}>{intlLowercase}</li>
                            <li className={passwordValidator.uppercase ? "valid" : "invalid"}>{intlUppercase}</li>
                            <li className={passwordValidator.specialCharacter ? "valid" : "invalid"}>{intlSpecialCharacters}</li>
                            <li className={passwordValidator.matching ? "valid" : "invalid"}>{intlPasswords}</li>
                        </ul>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlCountry}
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                value={userAccount.country}
                                name="country"
                                onChange={handleFormChange}
                                className="select"
                                size="small"
                                sx={{ width: '400px', minHeight: '55px' }}
                            >
                                {CountryListHelper.list.map((country, ix) => {
                                    return (
                                        <MenuItem key={ix} value={country.id}>{country.value}</MenuItem>
                                    )
                                })}
                            </Select>
                            <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                                {`${!countryIsValid && showErrors ? intlCountryRequired : ""}`}
                            </Typography>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlLanguage}
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                value={userAccount.locale}
                                name="locale"
                                onChange={handleFormChange}
                                className="select"
                                size="small"
                                sx={{ width: '400px', minHeight: '55px' }}
                            >
                                {LanguageListHelper.languages.map((language, ix) => {
                                    return (
                                        <MenuItem key={ix} value={language.locale}>{language.name}</MenuItem>
                                    )
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlLocationName}
                        </Typography>
                        <TextField
                            value={userAccount.fittingLocation}
                            margin="normal"
                            required
                            id="fittingLocation"
                            name="fittingLocation"
                            sx={{ width: '400px', textAlign: 'left', marginBottom: '10px !important' }}
                            onChange={handleFormChange}
                        />
                        <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                            {`${!locationIsValid && showErrors ? intlLocationRequired : ""}`}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography className='label' sx={{ color: 'black !important' }}>
                            {intlShipToNumber}
                        </Typography>
                        <TextField
                            value={userAccount.shipToNumber}
                            margin="normal"
                            required
                            id="shipToNumber"
                            name="shipToNumber"
                            onChange={handleFormChange}
                            sx={{ width: '400px', textAlign: 'left' }}
                        />
                    </Grid>
                </Grid>
            </Container>

            <Dialog
                open={showAuthenticatedPopup}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black', width: '400px' }}>
                        {intlThankYouRegistering} <br />&nbsp;<br />{intlConfirmationLink}
                    </DialogContentText>
                </DialogContent>
                <DialogActions className='appbar'>
                    <Button disableRipple variant='outlined' className='submit-button' onClick={handleDialogClose} sx={{ color: 'black', borderColor: 'black' }}>{intlContinue}</Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
